/**
 * MH-19 Phát ngay (mục 6.3 đặc tả giao diện) — hộp thoại ba bước rút gọn:
 * chọn bản tin đã duyệt, chọn phạm vi địa bàn, xác nhận. Sau khi gửi, chuyển
 * sang MH-23 để theo dõi phiên phát thay vì chỉ hiện thông báo thành công.
 *
 * "Số cụm đang phát nội dung khác" chỉ tính được qua
 * `POST /api/v1/broadcast/emergency/impact`, đòi quyền `broadcast.emergency`
 * mà người dùng MH-19 (`broadcast.play`) không nhất thiết có — dùng CƠ HỘI khi
 * có quyền, còn không thì hiển thị dấu gạch ngang (ghi ở NOTES-A0.md mục
 * Z2-B4 đề nghị A0 tách một điểm cuối khảo sát hệ quả dùng chung cho cả hai
 * quyền ở đợt sau).
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { vi, dienVao } from '../ngon-ngu/vi';
import { banTinApi, LOAI_BAN_TIN_CUM_LOA, type DongBanTin } from '../api/ban-tin-api';
import { phatSongApi, type BanKeHeQuaKhanCap } from '../api/phat-song-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BoChonPhamViDiaBan, dungCay, demDaChon, nhanPhamVi } from '../thanh-phan/BoChonPhamViDiaBan';
import { thietBiApi } from '../api/thiet-bi-api';
import { dungPhien } from '../trang-thai/ngu-canh-phien';

export interface MH19Props {
  api?: Pick<typeof phatSongApi, 'phatNgay' | 'khaoSatHeQua'>;
  banTinApiGoi?: Pick<typeof banTinApi, 'danhSach'>;
}

export function MH19PhatNgay({ api, banTinApiGoi }: MH19Props = {}): ReactNode {
  const goi = api ?? phatSongApi;
  const banTinGoi = banTinApiGoi ?? banTinApi;
  const phien = dungPhien();
  const dieuHuong = useNavigate();
  const [buoc, datBuoc] = useState<1 | 2 | 3>(1);
  const [banTinId, datBanTinId] = useState('');
  // Tài khoản cấp xã: phạm vi mặc định là xã được phân công.
  const [phamVi, datPhamVi] = useState<string[]>(() => (phien.khoaDiaBan ? [phien.khoaDiaBan.ma] : []));
  const [moPhamVi, datMoPhamVi] = useState(false);
  const [heQua, datHeQua] = useState<BanKeHeQuaKhanCap | 'khong-co-quyen' | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const banTinDuyet = dungTaiDuLieu<DongBanTin[]>({
    tai: async (signal) => {
      const kq = await banTinGoi.danhSach(
        { trangThai: 'da_duyet', loaiBanTin: LOAI_BAN_TIN_CUM_LOA, pageSize: 50 },
        signal,
      );
      return kq.items;
    },
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const cay = dungTaiDuLieu({
    tai: (signal) => thietBiApi.cayDiaBan({}, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const toiBuoc3 = async (): Promise<void> => {
    datBuoc(3);
    if (!phien.coQuyen('broadcast.emergency')) {
      datHeQua('khong-co-quyen');
      return;
    }
    try {
      datHeQua(await goi.khaoSatHeQua(phamVi.map((ma) => ({ DichID: ma }))));
    } catch {
      datHeQua('khong-co-quyen');
    }
  };

  const dem = cay.duLieu ? demDaChon(dungCay(cay.duLieu), new Set(phamVi)) : { xa: 0, thon: 0, cumLoa: 0 };

  const guiPhat = async (): Promise<void> => {
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await goi.phatNgay({ banTinId, phamVi: phamVi.map((ma) => ({ DichID: ma })) });
      dieuHuong(`/phat-song/phien/${(kq as { phienId?: string }).phienId ?? ''}`);
    } catch (e: unknown) {
      datLoi(e instanceof Error ? e.message : 'Không gửi được lệnh phát');
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-hinh" data-man-hinh="MH-19">
      <h1>{vi.phatSong.phatNgayTieuDe}</h1>

      {buoc === 1 ? (
        <section aria-label={vi.phatSong.buoc1ChonBanTin}>
          <h2>{vi.phatSong.buoc1ChonBanTin}</h2>
          <select
            value={banTinId}
            onChange={(e) => datBanTinId(e.target.value)}
            aria-label={vi.lichPhat.nhanBanTin}
          >
            <option value="">—</option>
            {(banTinDuyet.duLieu ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.tieuDe}
              </option>
            ))}
          </select>
          <button type="button" className="nut nut--chinh" disabled={!banTinId} onClick={() => datBuoc(2)}>
            Tiếp tục
          </button>
        </section>
      ) : null}

      {buoc === 2 ? (
        <section aria-label={vi.phatSong.buoc2ChonPhamVi}>
          <h2>{vi.phatSong.buoc2ChonPhamVi}</h2>
          <p>{phamVi.length === 0 ? vi.bienTap.chuaChonPhamVi : nhanPhamVi(phamVi, phien.tenDiaBanTheoMa)}</p>
          <button type="button" className="nut" onClick={() => datMoPhamVi(true)}>
            {vi.bienTap.chonPhamVi}
          </button>
          <button
            type="button"
            className="nut nut--chinh"
            disabled={phamVi.length === 0}
            onClick={() => void toiBuoc3()}
          >
            Tiếp tục
          </button>
        </section>
      ) : null}

      {buoc === 3 ? (
        <section aria-label={vi.phatSong.buoc3XacNhan}>
          <h2>{vi.phatSong.buoc3XacNhan}</h2>
          <p data-vung="so-cum-trong-pham-vi">{dienVao(vi.phatSong.soCumTrongPhamVi, { so: dem.cumLoa })}</p>
          {heQua === 'khong-co-quyen' ? (
            <p data-vung="so-cum-dang-phat-khac">{vi.banTin.chuaCo}</p>
          ) : heQua ? (
            <p data-vung="so-cum-dang-phat-khac">
              {dienVao(vi.phatSong.soCumDangPhatKhac, { so: heQua.soCumDangPhatNoiDungKhac })}
            </p>
          ) : (
            <p>{vi.phatSong.heQuaDangTai}</p>
          )}
          {loi ? (
            <p role="alert" data-vung="loi-phat-ngay">
              {loi}
            </p>
          ) : null}
          <button type="button" className="nut nut--chinh" disabled={dangGui} onClick={() => void guiPhat()}>
            {vi.phatSong.xacNhanPhat}
          </button>
        </section>
      ) : null}

      {moPhamVi ? (
        <BoChonPhamViDiaBan
          giaTri={phamVi}
          onXacNhan={(ds) => {
            datPhamVi(ds);
            datMoPhamVi(false);
          }}
          onHuy={() => datMoPhamVi(false)}
        />
      ) : null}
    </div>
  );
}
